import * as Sentry from "@sentry/react";

/**
 * A SIGNED STORAGE URL'S QUERY *IS* ITS BEARER TOKEN, and the SDK's default instrumentation sends
 * request URLs twice over: once as a `fetch`/`xhr` breadcrumb attached to every event, and once as
 * the description of the `http.client` span the tracing integration opens. Supabase's
 * `createSignedUrl` returns exactly such a URL — `certificateOnFile` fetches the filed certificate
 * with one — so anything that leaves this page carrying a full URL is shipping a credential to a
 * third party. The path is the useful half and the query is the secret half, so the query goes.
 *
 * BOTH HOOKS, BECAUSE THEY ARE TWO DIFFERENT PIPES. `beforeBreadcrumb` cannot see spans and
 * `beforeSendSpan` cannot see breadcrumbs; stripping in one of them leaves the other leaking.
 */
const withoutQuery = (url: string) => url.split('?')[0];

Sentry.init({
  dsn: import.meta.env.VITE_SENTRY_DSN,
  enabled: !!import.meta.env.VITE_SENTRY_DSN,
  integrations: [
    Sentry.browserTracingIntegration(),
  ],
  beforeBreadcrumb(crumb) {
    if ((crumb.category === 'fetch' || crumb.category === 'xhr') && typeof crumb.data?.url === 'string') {
      crumb.data.url = withoutQuery(crumb.data.url);
    }
    return crumb;
  },
  // The span's own copy of the same string: `GET https://…/object/sign/…?token=…` is the
  // description, and the URL attributes beside it carry it again.
  beforeSendSpan(span) {
    if (span.description) span.description = withoutQuery(span.description);
    for (const key of ['url', 'http.url', 'server.address'] as const) {
      const value = span.data?.[key];
      if (typeof value === 'string') span.data[key] = withoutQuery(value);
    }
    return span;
  },
  // Capture 100% of errors
  sampleRate: 1.0,
  // Performance: capture 10% of transactions (tune down in production)
  tracesSampleRate: 0.1,
  environment: import.meta.env.VITE_ENVIRONMENT ?? import.meta.env.MODE,
});

export { Sentry };
